/**
 * Input formatting for CCD forms: masks that reshape a value while it is typed,
 * and tidy functions applied when a field is left.
 *
 * Formatting only reduces errors; validation rules still decide what is valid.
 * Every function here keeps data it does not understand: a mask reshapes a
 * value only when its characters and digit count fit the pattern, and returns
 * null otherwise so the value stays exactly as typed, pasted, or saved.
 */

interface MaskState {
  value: string
  caret: number
}

/** One input event: the value and caret after the browser applied the edit, and the value before it. */
export interface MaskEdit extends MaskState {
  previous: string
  inputType: string
}

/** Returns the reshaped value and caret, or null to leave the value as typed. */
export type Mask = (edit: MaskEdit) => MaskState | null

/** maxlength for free-text fields. Masked fields cap in code, because the attribute truncates pastes. */
export const maxLength = { text: 250, longText: 2000 } as const

interface DigitPattern {
  /** Characters a value may contain and still be reshaped. */
  fits: (value: string) => boolean
  /** Most digits the value may hold. */
  maxDigits: (digits: string) => number
  /** Groups digits for display. Never ends with a separator. Returns the display and how many leading digits it dropped. */
  format: (digits: string) => { value: string; dropped: number }
  /** Reject a typed character that doesn't fit, instead of leaving the value as typed. */
  strictTyping: boolean
}

function digitsIn(text: string) {
  return text.replace(/\D/g, '')
}

/** Index just after the nth digit of text; 0 for n <= 0. */
function caretAfterDigit(text: string, n: number): number {
  if (n <= 0) return 0
  let seen = 0
  for (let index = 0; index < text.length; index++) {
    const char = text.charAt(index)
    if (char >= '0' && char <= '9' && ++seen === n) return index + 1
  }
  return text.length
}

function isTyping(inputType: string) {
  return inputType === 'insertText' || inputType === 'insertCompositionText'
}

function revert(edit: MaskEdit): MaskState {
  return {
    value: edit.previous,
    caret: Math.max(0, edit.caret - (edit.value.length - edit.previous.length)),
  }
}

function digitMask(pattern: DigitPattern): Mask {
  return (edit) => {
    const previousFits =
      pattern.fits(edit.previous) &&
      digitsIn(edit.previous).length <= pattern.maxDigits(digitsIn(edit.previous))
    if (!pattern.fits(edit.value))
      return pattern.strictTyping && isTyping(edit.inputType) && previousFits ? revert(edit) : null
    // A space typed at the end may start an extension or a note; keep it until
    // the next character shows whether the value still fits. Tidying on leave trims it.
    if (isTyping(edit.inputType) && edit.caret === edit.value.length && /\s$/.test(edit.value))
      return null

    let digits = digitsIn(edit.value)
    let caretDigits = digitsIn(edit.value.slice(0, edit.caret)).length
    // Deleting only a separator would leave the digits unchanged and the separator
    // would come straight back, so delete the digit beside it instead.
    if (edit.value !== edit.previous && digits === digitsIn(edit.previous)) {
      if (edit.inputType === 'deleteContentBackward' && caretDigits > 0) {
        digits = digits.slice(0, caretDigits - 1) + digits.slice(caretDigits)
        caretDigits--
      } else if (edit.inputType === 'deleteContentForward' && caretDigits < digits.length) {
        digits = digits.slice(0, caretDigits) + digits.slice(caretDigits + 1)
      }
    }
    if (digits.length > pattern.maxDigits(digits))
      return isTyping(edit.inputType) && previousFits ? revert(edit) : null

    const { value, dropped } = pattern.format(digits)
    return { value, caret: caretAfterDigit(value, caretDigits - dropped) }
  }
}

/** EIN: digits only, hyphen after two, at most nine digits. `123456789` → `12-3456789`. */
export const einMask: Mask = digitMask({
  fits: (value) => /^[\d\s-]*$/.test(value),
  maxDigits: () => 9,
  format: (digits) => ({
    value: digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits,
    dropped: 0,
  }),
  strictTyping: true,
})

/** D-U-N-S number: digits only, hyphens after the 2nd and 5th digit, at most nine digits. `123456789` → `12-345-6789`. */
export const dunsMask: Mask = digitMask({
  fits: (value) => /^[\d\s-]*$/.test(value),
  maxDigits: () => 9,
  format: (digits) => {
    if (digits.length <= 2) return { value: digits, dropped: 0 }
    if (digits.length <= 5) return { value: `${digits.slice(0, 2)}-${digits.slice(2)}`, dropped: 0 }
    return { value: `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`, dropped: 0 }
  },
  strictTyping: true,
})

function nationalPhone(digits: string) {
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * US phone: `417-555-0100`. A leading 1 is the country code (no area code starts
 * with 1): shown as `1 417-5…` while typing and dropped once all ten digits are
 * in, so `+1 (417) 555-0100` and `14175550100` both become `417-555-0100`.
 * A `+` followed by another country code, letters, or an extension leave the value as typed.
 */
export const phoneMask: Mask = digitMask({
  fits: (value) => /^\s*(\+\s*1)?[\d\s().-]*$/.test(value),
  maxDigits: (digits) => (digits.startsWith('1') ? 11 : 10),
  format: (digits) => {
    if (!digits.startsWith('1')) return { value: nationalPhone(digits), dropped: 0 }
    const national = digits.slice(1)
    if (national.length === 10) return { value: nationalPhone(national), dropped: 1 }
    return { value: national ? `1 ${nationalPhone(national)}` : '1', dropped: 0 }
  },
  strictTyping: false,
})

/** Applies a mask to an input event on its element and returns the value to store. */
export function applyMask(event: Event, mask: Mask, previous: unknown): string {
  const element = event.target as HTMLInputElement
  // A plain Event('input') carries no inputType; treat it as an ordinary edit.
  const input = event instanceof InputEvent ? event : undefined
  if (input?.isComposing === true) return element.value
  const result = mask({
    value: element.value,
    caret: element.selectionStart ?? element.value.length,
    previous: typeof previous === 'string' ? previous : '',
    inputType: input?.inputType ?? '',
  })
  if (!result) return element.value
  if (element.value !== result.value) element.value = result.value
  if (document.activeElement === element) element.setSelectionRange(result.caret, result.caret)
  return result.value
}

// ---- Typing filters for numeric fields ---------------------------------------------------------

/** Whether typed text may be inserted into a whole-number or money field. Pastes are never filtered. */
export function allowsTyped(kind: 'whole' | 'money', data: string | null): boolean {
  if (data === null) return true
  return kind === 'whole' ? /^\d*$/.test(data) : /^[\d.,$\s]*$/.test(data)
}

/** Strips `$`, spaces, and correctly placed thousands separators: `$1,250.50` → `1250.50`. Anything else is returned unchanged. */
export function cleanMoney(text: string): string {
  const trimmed = text.trim()
  if (!/^\$?\s*(\d{1,3}(,\d{3})+|\d*)(\.\d*)?$/.test(trimmed)) return text
  return trimmed.replace(/[$,\s]/g, '')
}

/** Display for a stored amount: 1250 → `1,250`, 1250.5 → `1,250.50`. Empty stays empty; anything else, including more than two decimals, is shown as stored. */
export function formatMoney(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return ''
  if (value === '') return ''
  const text = String(value)
  if (typeof value !== 'number' && !/^\d*\.?\d*$/.test(text)) return text
  const amount = Number(value)
  if (!Number.isFinite(amount)) return text
  const decimals = text.includes('.') ? (text.split('.')[1] ?? '').length : 0
  if (decimals > 2) return text
  return amount.toLocaleString(
    'en-US',
    decimals > 0
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumFractionDigits: 0 },
  )
}

// ---- Tidying when a field is left --------------------------------------------------------------

export type TidyKind = 'text' | 'email' | 'zips' | 'none'

function uniqueJoined(parts: string[]) {
  return [...new Set(parts)].join(', ')
}

/** Splits a ZIP list on commas, newlines, and (only when every piece is a ZIP) spaces; trims, drops duplicates, joins with `, `. */
export function tidyZipList(value: string): string {
  const words = value.split(/[\s,]+/).filter(Boolean)
  if (words.length > 0 && words.every((word) => /^\d{5}(-\d{4})?$/.test(word)))
    return uniqueJoined(words)
  return uniqueJoined(
    value
      .split(/[\n,]/)
      .map((part) => part.trim())
      .filter(Boolean),
  )
}

/** Tidies a string for its kind. Non-strings are returned unchanged. */
export function tidy(kind: TidyKind, value: unknown): unknown {
  if (typeof value !== 'string' || kind === 'none') return value
  if (kind === 'email') return value.trim().toLowerCase()
  if (kind === 'zips') return tidyZipList(value)
  return value.trim()
}
